import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import StartButton from './StartButton';
import { cn } from '@/lib/utils';

const navItems = [
  { label: 'The Pilot', to: '/pilot' },
  { label: 'Use Cases', to: '/use-cases' },
  { label: 'How It Works', to: '/#how-it-works' },
  { label: 'Consulting', to: '/consulting' },
  { label: 'About', to: '/about' },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'rounded-sm py-1 font-mono text-xs uppercase tracking-[0.1em] transition-colors hover:text-softwhite',
    isActive ? 'text-goldcore' : 'text-mist',
  );

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setIsOpen(false), [location.pathname, location.hash]);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300',
        isScrolled || isOpen
          ? 'border-rule bg-knightblack/85 backdrop-blur-md'
          : 'border-rule/60 bg-knightblack/70 backdrop-blur-md',
      )}
    >
      <nav
        aria-label="Main"
        className="mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-6 px-4 sm:px-6"
      >
        <Link to="/" className="flex items-center gap-2.5 rounded-sm" aria-label="GoldCrusade home">
          <img src="/lovable-uploads/0aa296c2-39b8-4316-97b8-2a5754cf1180.png" alt="" className="h-9 w-auto" />
          <span className="font-serif text-[1.4rem] leading-none tracking-[-0.01em] text-softwhite">GoldCrusade</span>
        </Link>

        <ul className="hidden items-center gap-7 lg:flex">
          {navItems.map((item) => (
            <li key={item.to}>
              {/* Hash links are never "active" — the section lives on the homepage. */}
              {item.to.includes('#') ? (
                <Link to={item.to} className={linkClass({ isActive: false })}>
                  {item.label}
                </Link>
              ) : (
                <NavLink to={item.to} className={linkClass}>
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <StartButton size="sm" className="hidden sm:inline-flex" />
          <button
            type="button"
            className="rounded-md p-2 text-goldcore lg:hidden"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setIsOpen((open) => !open)}
          >
            {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div id="mobile-menu" className="border-t border-rule bg-knightblack lg:hidden">
          <ul className="mx-auto max-w-7xl space-y-1 px-4 py-4 sm:px-6">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="block rounded-sm px-2 py-3 font-mono text-sm uppercase tracking-[0.1em] text-softwhite hover:text-goldcore"
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="pt-3">
              <StartButton className="w-full" />
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
